"use client";

import { useAnalyticsCaptureReady } from "@analytics/lib/analytics-capture-ready-context";
import { recordIdentityLifecycleSignalOnceForSqid } from "@analytics/lib/auth-lifecycle";
import {
	clearAuthPendingSessionIntent,
	readAuthPendingSessionIntent,
} from "@analytics/lib/auth-session-pending-intent";
import { linkSqidFromEmailDeepLink } from "@analytics/lib/identity-resolution";
import { persistPosthogDistinctIdLink } from "@analytics/lib/persist-posthog-distinct-id-link";
import { ActiveAnalyticsProvider } from "@analytics/lib/resolve-provider";
import { syncAuthIdentityCookieAndVisitor } from "@analytics/lib/sync-auth-identity-cookie";
import { useAuthAnalytics } from "@analytics/lib/use-auth-analytics";
import { readVisitorBootstrapSnapshot } from "@analytics/lib/visitor-bootstrap-cache";
import * as Posthog from "@analytics/provider/posthog";
import { IDENTITY_LIFECYCLE_SIGNALS } from "@repo/identity";
import { useSession } from "@saas/auth/hooks/use-session";
import { useConsent } from "@shared/hooks/cookie-consent";
import { orpcClient } from "@shared/lib/orpc-client";
import { useEffect, useRef } from "react";

export function SessionIdentityIdentify() {
	const { user, session, loaded } = useSession();
	const { hasConsent, status } = useConsent();
	const captureReady = useAnalyticsCaptureReady();
	const { authSessionEstablished } = useAuthAnalytics();
	const lastSessionIdRef = useRef<string | null>(null);
	const sessionEstablishedTrackedRef = useRef<string | null>(null);

	useEffect(() => {
		if (!loaded || !user || !session) {
			return;
		}
		if (status === "unknown" || !hasConsent("analytics") || !captureReady) {
			return;
		}
		if (lastSessionIdRef.current === session.id) {
			return;
		}
		lastSessionIdRef.current = session.id;

		void (async () => {
			const sqid =
				typeof user.sqid === "string" && user.sqid.trim().length > 0
					? user.sqid.trim()
					: null;
			if (!sqid) {
				return;
			}

			if (sessionEstablishedTrackedRef.current !== session.id) {
				sessionEstablishedTrackedRef.current = session.id;
				const pendingIntent = readAuthPendingSessionIntent();
				authSessionEstablished({
					flow: pendingIntent?.flow ?? "login",
					method: pendingIntent?.method ?? "unknown",
					...(pendingIntent?.provider
						? { provider: pendingIntent.provider }
						: {}),
					had_pending_intent: pendingIntent != null,
				});
				recordIdentityLifecycleSignalOnceForSqid(
					sqid,
					IDENTITY_LIFECYCLE_SIGNALS.authSessionEstablished,
					`sq:identity:lifecycle:auth_session_established:${session.id}`,
				);
				if (pendingIntent != null) {
					clearAuthPendingSessionIntent();
				}
			}

			await syncAuthIdentityCookieAndVisitor(sqid);
			const cachedTraits = readVisitorBootstrapSnapshot(sqid);
			const traits =
				cachedTraits ??
				(
					await orpcClient.identity.resolveBootstrap({
						sqid,
					})
				).traitsForPosthog;
			const linkResult = await linkSqidFromEmailDeepLink({
				sqid,
				traits,
				getCurrentDistinctId: async () => {
					if (ActiveAnalyticsProvider !== Posthog) {
						return sqid;
					}
					return Posthog.getDistinctId();
				},
			});
			await persistPosthogDistinctIdLink({
				sqid,
				getCurrentDistinctId: async () => {
					if (ActiveAnalyticsProvider !== Posthog) {
						return sqid;
					}
					return Posthog.getDistinctId();
				},
				priorDistinctId: linkResult.priorDistinctId,
			}).catch(() => undefined);
		})();
	}, [
		authSessionEstablished,
		captureReady,
		hasConsent,
		loaded,
		session,
		status,
		user,
	]);

	return null;
}
